import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTACT_KIND_LABELS, MERGE_FIELD_LABELS, MERGE_FIELDS, type Contact, type ContactMergeResult, type MergeField, type Paginated } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './contacts.css';

type Choice = 'target' | 'source' | 'both';

const empty = (v: string | null | undefined) => v === null || v === undefined || v === '';

/**
 * Zwei Kontakte zusammenführen (KON-049). Der Kontakt in der Adresse bleibt, der aus „mit“ geht darin auf. Das Büro wählt je Feld, welche Angabe
 * bleibt. Ohne Wahl bleibt die Angabe des bleibenden Kontakts, und nur ein leeres Feld füllt sich aus dem anderen. Die Anfragen und, bei Firmen,
 * die Ansprechpartner des aufgelösten Kontakts gehen auf den bleibenden über. Ein Zusammenführen lässt sich nicht rückgängig machen.
 */
export function ContactMergePage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const sourceId = params.get('mit') ?? '';
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can, hasModule } = useSession();
  const [chosen, setChosen] = useState<Partial<Record<MergeField, Choice>>>({});
  const [search, setSearch] = useState('');

  const target = useQuery({ queryKey: ['contact', id], queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${id}`)).data });
  const source = useQuery({ queryKey: ['contact', sourceId], queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${sourceId}`)).data, enabled: !!sourceId, retry: false });

  const companyIds = [...new Set([target.data?.companyId, source.data?.companyId].filter((x): x is string => !!x))];
  const companies = useQuery({
    queryKey: ['contact-names', companyIds.join()],
    queryFn: async () => Object.fromEntries(await Promise.all(companyIds.map(async (c) => [c, (await api<{ data: Contact }>('GET', `/contacts/${c}`)).data.name] as const))),
    enabled: companyIds.length > 0,
  });

  // Wie viel mit dem aufgelösten Kontakt übergeht
  const inquiriesAllowed = hasModule('inquiries') && can('inquiries.read');
  const sourceInquiries = useQuery({
    queryKey: ['contact-inquiry-count', sourceId],
    queryFn: async () => (await api<Paginated<unknown>>('GET', `/inquiries?contactId=${sourceId}&perPage=1`)).meta.total,
    enabled: !!sourceId && inquiriesAllowed,
  });
  const sourcePeople = useQuery({
    queryKey: ['contact-people-count', sourceId],
    queryFn: async () => (await api<Paginated<unknown>>('GET', `/contacts?companyId=${sourceId}&perPage=1`)).meta.total,
    enabled: !!sourceId && source.data?.kind === 'company',
  });

  // Auswahl des anderen Kontakts, solange keiner gewählt ist
  const candidates = useQuery({
    queryKey: ['contacts', { merge: id, q: search }],
    queryFn: async () => (await api<Paginated<Contact>>('GET', `/contacts?kind=${target.data!.kind}&q=${encodeURIComponent(search)}&perPage=8&sort=name`)).data,
    enabled: !sourceId && !!target.data && search.trim().length >= 2,
  });

  const merge = useMutation({
    mutationFn: () => api<{ data: ContactMergeResult }>('POST', `/contacts/${id}/merge`, { sourceId, choose: chosen }),
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['contacts'] });
      client.invalidateQueries({ queryKey: ['contact-duplicate-groups'] });
      client.invalidateQueries({ queryKey: ['contact-duplicates'] });
      client.invalidateQueries({ queryKey: ['contact-inquiries'] });
      client.removeQueries({ queryKey: ['contact', sourceId] });
      client.setQueryData(['contact', id], res.data.contact);
      navigate(`/kontakte/${id}`, { replace: true, state: { merged: { name: source.data?.name, inquiries: res.data.movedInquiries, people: res.data.movedPeople } } });
    },
  });

  if (target.isLoading) return <p className="muted">Wird geladen …</p>;
  if (target.isError || !target.data) {
    return (
      <div className="card narrow">
        <h1>Kontakt nicht gefunden</h1>
        <Link to="/kontakte">Zur Übersicht</Link>
      </div>
    );
  }
  const t = target.data;
  const fields = MERGE_FIELDS.filter((f) => f !== 'companyId' || t.kind === 'person');
  const display = (f: MergeField, c: Contact): string => {
    if (f === 'companyId') return c.companyId ? (companies.data?.[c.companyId] ?? 'Wird geladen …') : 'Keine (Privatperson)';
    const v = c[f];
    return empty(v) ? '–' : v!;
  };
  // Ohne Wahl: der bleibende Kontakt, nur bei dessen leerem Feld der andere (wie auf dem Server)
  const effective = (f: MergeField, s: Contact): Choice => chosen[f] ?? (empty(t[f]) && !empty(s[f]) ? 'source' : 'target');
  const error = merge.error instanceof ApiError ? merge.error.message : null;

  // Schritt 1: den anderen Kontakt wählen
  if (!sourceId || source.isError) {
    return (
      <>
        <p><Link to={`/kontakte/${id}`} className="back-link">← Zurück zum Kontakt</Link></p>
        <div className="page-head"><h1>{t.name} zusammenführen</h1></div>
        <section className="card">
          <h2>Mit welchem Kontakt?</h2>
          <p className="muted small">Es lassen sich nur {t.kind === 'person' ? 'Personen mit Personen' : 'Firmen mit Firmen'} zusammenführen. Suchen Sie den anderen Kontakt nach Name, E-Mail oder Telefon.</p>
          {source.isError && <div className="alert alert--error" role="alert">Dieser Kontakt ist nicht mehr vorhanden.</div>}
          <label className="field">
            <span>Suche</span>
            <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Mindestens zwei Zeichen" autoComplete="off" autoFocus />
          </label>
          {candidates.data?.length === 0 && <p className="muted">Kein passender Kontakt.</p>}
          <ul className="contact-options">
            {(candidates.data ?? []).filter((c) => c.id !== id).map((c) => (
              <li key={c.id}>
                <span className="contact-options__who">
                  <strong>{c.name}</strong>
                  <div className="muted small">{[c.email, c.phone, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</div>
                </span>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setParams({ mit: c.id })}>Wählen</button>
              </li>
            ))}
          </ul>
        </section>
      </>
    );
  }

  if (source.isLoading || !source.data) return <p className="muted">Wird geladen …</p>;
  const s = source.data;
  if (s.kind !== t.kind) {
    return (
      <div className="card narrow">
        <h1>Nicht möglich</h1>
        <p>Eine Person und eine Firma lassen sich nicht zusammenführen.</p>
        <Link to={`/kontakte/${id}`}>Zurück</Link>
      </div>
    );
  }
  const inquiryCount = inquiriesAllowed ? (sourceInquiries.data ?? 0) : 0;
  const peopleCount = s.kind === 'company' ? (sourcePeople.data ?? 0) : 0;
  const moves = [inquiryCount > 0 ? `${inquiryCount} ${inquiryCount === 1 ? 'Anfrage' : 'Anfragen'}` : null, peopleCount > 0 ? `${peopleCount} Ansprechpartner` : null].filter(Boolean);
  const verb = inquiryCount + peopleCount === 1 ? 'geht' : 'gehen';

  return (
    <>
      <p><Link to={`/kontakte/${id}`} className="back-link">← Zurück zum Kontakt</Link></p>
      <div className="page-head">
        <h1>Kontakte zusammenführen</h1>
        <span className="badge">{CONTACT_KIND_LABELS[t.kind]}</span>
        <Link className="btn btn--secondary btn--sm page-head__action" to={`/kontakte/${s.id}/zusammenfuehren?mit=${t.id}`}>Seiten tauschen</Link>
      </div>

      <div className="card merge-intro">
        <div>
          <span className="muted small">Bleibt</span>
          <div><Link to={`/kontakte/${t.id}`} className="strong-link">{t.name}</Link></div>
        </div>
        <span className="arrow" aria-hidden="true">←</span>
        <div>
          <span className="muted small">Geht auf und wird gelöscht</span>
          <div><Link to={`/kontakte/${s.id}`} className="strong-link">{s.name}</Link></div>
        </div>
      </div>

      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          merge.mutate();
        }}
      >
        <h2>Welche Angaben bleiben?</h2>
        <p className="muted small">Wo beide Kontakte dasselbe haben, steht „gleich“. Wählen Sie bei den anderen, welche Angabe der bleibende Kontakt behält.</p>
        {error && <div className="alert alert--error" role="alert">{error}</div>}

        {fields.map((f) => {
          const tv = display(f, t);
          const sv = display(f, s);
          if (tv === sv) {
            return (
              <div key={f} className="merge-row merge-row--same">
                <span className="merge-row__label">{MERGE_FIELD_LABELS[f]}</span>
                <span>{tv}</span>
                <span className="muted small">gleich</span>
              </div>
            );
          }
          const current = effective(f, s);
          const option = (value: Choice, caption: string, text: string) => (
            <label className={`merge-option${current === value ? ' is-chosen' : ''}`}>
              <input type="radio" name={f} value={value} checked={current === value} onChange={() => setChosen((c) => ({ ...c, [f]: value }))} />
              <span>
                <small className="muted">{caption}</small>
                <span className="merge-option__value">{text}</span>
              </span>
            </label>
          );
          return (
            <div key={f} className="merge-row" role="radiogroup" aria-label={MERGE_FIELD_LABELS[f]}>
              <span className="merge-row__label" aria-hidden="true">{MERGE_FIELD_LABELS[f]}</span>
              {option('target', 'Vom bleibenden Kontakt', tv)}
              {option('source', 'Vom aufgelösten Kontakt', sv)}
              {f === 'note' && !empty(t.note) && !empty(s.note) && option('both', 'Beide untereinander', 'Beide Notizen')}
            </div>
          );
        })}

        <div className="alert alert--warn">
          <strong>Das lässt sich nicht rückgängig machen.</strong>{' '}
          {moves.length > 0 ? `${moves.join(' und ')} von „${s.name}“ ${verb} auf „${t.name}“ über. ` : ''}„{s.name}“ wird danach ausgeblendet. Das Zusammenführen steht im Protokoll.
        </div>
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={merge.isPending}>{merge.isPending ? 'Wird zusammengeführt …' : 'Zusammenführen'}</button>
          <Link className="btn btn--secondary" to={`/kontakte/${id}`}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
